import React from 'react';
import { AlertTriangle, Clock, Shield } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const SuspensionBanner = ({ suspensionInfo, accountStatus }) => {
    const { t } = useTranslation();

    // Only show if account is suspended
    if (accountStatus !== 'suspended' || !suspensionInfo) {
        return null;
    }

    const { suspendedUntil, reason, violationType } = suspensionInfo;

    // Calculate time remaining
    const getTimeRemaining = () => {
        if (!suspendedUntil) return t('admin.moderation.permanently');

        const now = new Date();
        const endDate = suspendedUntil.toDate ? suspendedUntil.toDate() : new Date(suspendedUntil);
        const diffMs = endDate - now;

        if (diffMs <= 0) return t('admin.moderation.expired');

        const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
        const diffHours = Math.ceil(diffMs / (1000 * 60 * 60));

        if (diffDays > 1) {
            return t('admin.moderation.days', { count: diffDays });
        } else if (diffHours > 1) {
            return t('admin.moderation.hours', { count: diffHours });
        } else {
            return t('admin.moderation.less_than_hour');
        }
    };

    const timeRemaining = getTimeRemaining();

    return (
        <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-red-950 to-red-900 border-b-4 border-red-500 shadow-2xl">
            <div className="max-w-6xl mx-auto px-6 py-4">
                <div className="flex items-center gap-4">
                    {/* Icon */}
                    <div className="p-3 bg-red-500/20 rounded-xl border border-red-500/50 animate-pulse">
                        <AlertTriangle className="text-red-200" size={28} />
                    </div>

                    {/* Content */}
                    <div className="flex-1">
                        <h2 className="text-xl font-black text-white uppercase tracking-wider mb-1">
                            {t('admin.moderation.account_suspended')}
                        </h2>
                        <div className="flex items-center gap-4 text-sm text-red-100">
                            <div className="flex items-center gap-2">
                                <Clock size={14} />
                                <span className="font-bold">
                                    {timeRemaining === 'expirada'
                                        ? t('admin.moderation.suspension_expired')
                                        : t('admin.moderation.time_remaining', { time: timeRemaining })
                                    }
                                </span>
                            </div>
                            {suspendedUntil && (
                                <div className="hidden sm:block text-xs opacity-75">
                                    {t('admin.moderation.until_label')}: {(suspendedUntil.toDate ? suspendedUntil.toDate() : new Date(suspendedUntil)).toLocaleString()}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Info Button */}
                    <button
                        onClick={() => {
                            alert(
                                t('admin.moderation.suspension_alert_title') + '\n\n' +
                                t('admin.moderation.suspension_alert_reason', { reason: reason || t('common.not_specified') }) + '\n' +
                                t('admin.moderation.suspension_alert_type', { type: violationType || 'N/A' }) + '\n' +
                                t('admin.moderation.suspension_alert_duration', { duration: timeRemaining }) + '\n\n' +
                                t('admin.moderation.suspension_alert_restrictions') + '\n\n' +
                                t('admin.moderation.suspension_alert_contact')
                            );
                        }}
                        className="px-4 py-2 bg-white/10 hover:bg-white/20 border border-white/30 rounded-lg text-white font-bold text-sm uppercase tracking-wider transition-all"
                    >
                        <Shield size={16} className="inline mr-2" />
                        {t('admin.moderation.more_info')}
                    </button>
                </div>

                {/* Reason (if provided) */}
                {reason && (
                    <div className="mt-3 pt-3 border-t border-red-400/20">
                        <p className="text-sm text-red-100">
                            <span className="font-bold uppercase tracking-wider">
                                {t('admin.moderation.reason')}:
                            </span>{' '}
                            {reason}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SuspensionBanner;
